import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  Shield,
  Ban,
  Heart,
  Crown,
  Lock,
  ArrowRight,
  Loader2
} from 'lucide-react';
import HowItWorks from '../components/home/HowItWorks';
import SampleProfiles from '../components/home/SampleProfiles';
import WhyPremium from '../components/home/WhyPremium';
import PricingSection from '../components/home/PricingSection';
import FAQSection from '../components/home/FAQSection';

// Hero profile grid: 5 locked (full blur) + 1 unlocked (semi blur).
const heroPhotoFallbacks = [
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=533&fit=crop',
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=533&fit=crop',
  'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=400&h=533&fit=crop',
  'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400&h=533&fit=crop',
  'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=533&fit=crop',
  'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&h=533&fit=crop',
];

const heroProfiles = [
  { name: 'Priya (24, Delhi)', tags: 'Friendly | Creative', blurred: true, photo: heroPhotoFallbacks[0] },
  { name: 'Rahul (28, Mumbai)', tags: 'Chill | Gaming', blurred: true, photo: heroPhotoFallbacks[1] },
  { name: 'Kavya (26, Chennai)', tags: 'Travel | Music', blurred: true, photo: heroPhotoFallbacks[2] },
  { name: 'Riya (25, Mumbai)', tags: 'Friendly | Talkative | Traveler', verified: true, available: true, photo: heroPhotoFallbacks[3] },
  { name: 'Neha (27, Hyderabad)', tags: 'Art | Coffee', blurred: true, photo: heroPhotoFallbacks[4] },
  { name: 'Sara (22, Pune)', tags: 'Movies | Fitness', blurred: true, photo: heroPhotoFallbacks[5] },
];

const HomePage = () => {
  const { user, userProfile } = useAuth();
  const [counts, setCounts] = useState({ verified: 0, paid: 0 });
  const [countsLoading, setCountsLoading] = useState(true);

  const isPremium = userProfile?.subscriptionStatus === 'premium';

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const { count: verifiedCount } = await supabase
          .from('user_profiles')
          .select('id', { count: 'exact', head: true })
          .eq('role', 'service_provider')
          .eq('is_verified', true);

        const { count: paidCount } = await supabase
          .from('user_profiles')
          .select('id', { count: 'exact', head: true })
          .eq('subscription_status', 'premium');

        setCounts({ 
          verified: verifiedCount || 150, // Fallback to a base number if DB is fresh
          paid: paidCount || 85 
        });
      } catch (err) {
        console.error('Error fetching counts:', err);
      } finally {
        setCountsLoading(false);
      }
    };

    fetchCounts();
  }, []);

  return (
    <div className="bg-[#0f172a]">
      {/* Hero – dark theme */}
      <section className="relative overflow-hidden pt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column */}
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-pink/10 border border-brand-pink/20 text-brand-pink text-[10px] font-black uppercase tracking-widest mb-6">
                 <div className="w-1.5 h-1.5 rounded-full bg-brand-pink animate-ping" />
                 Platform Status: Online
              </div>
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] mb-6">
                Find a verified friend for chats or fun—<span className="text-brand-pink">anytime.</span>
              </h1>
              <ul className="space-y-5 text-white/80 mb-10">
                <li className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white/5 border border-white/10 text-brand-pink shadow-xl">
                    <Shield className="h-5 w-5" />
                  </div>
                  <span className="font-medium text-lg">100% Verified Profiles</span>
                </li>
                <li className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white/5 border border-white/10 text-red-400 shadow-xl">
                    <Ban className="h-5 w-5" />
                  </div>
                  <span className="font-medium text-lg">No Free Users = No Junk</span>
                </li>
                <li className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white/5 border border-white/10 text-brand-pink shadow-xl">
                    <Heart className="h-5 w-5" />
                  </div>
                  <span className="font-medium text-lg">Real People. Real Connections.</span>
                </li>
              </ul>

              <div className="flex flex-col sm:flex-row items-center gap-4">
                {user ? (
                   isPremium ? (
                    <Link
                      to="/dashboard"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-green-500 text-white font-black text-lg hover:bg-green-600 transition-all shadow-xl shadow-green-500/20 transform hover:-translate-y-1 active:translate-y-0"
                    >
                      <Crown className="h-6 w-6" />
                      PREMIUM ACTIVE – GO TO DASHBOARD
                    </Link>
                   ) : (
                    <Link
                      to="/payment"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-brand-pink text-white font-black text-lg hover:bg-brand-pink-dark transition-all shadow-xl shadow-brand-pink/25 transform hover:-translate-y-1 active:translate-y-0"
                    >
                      <Crown className="h-6 w-6" />
                      UPGRADE NOW – ₹2500
                    </Link>
                   )
                ) : (
                  <Link
                    to="/register"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-brand-pink text-white font-black text-lg hover:bg-brand-pink-dark transition-all shadow-xl shadow-brand-pink/25 transform hover:-translate-y-1 active:translate-y-0"
                  >
                    <Crown className="h-6 w-6" />
                    JOIN NOW – ₹2500 (MONTHLY)
                  </Link>
                )}
                
                <Link
                  to="/how-it-works"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-white/5 border border-white/10 text-white font-bold hover:bg-white/10 transition-all"
                >
                  KNOW MORE
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>

            {/* Right Column Grid */}
            <div className="relative">
              <div className="grid grid-cols-3 gap-3">
                {heroProfiles.map((p, i) => (
                  <div
                    key={i}
                    className={`rounded-2xl overflow-hidden border ${
                      p.verified
                        ? 'bg-slate-800/80 border-brand-pink/40 ring-2 ring-brand-pink/20'
                        : 'bg-slate-800/40 border-white/5'
                    }`}
                  >
                    <div className="aspect-[3/4] relative flex items-center justify-center bg-slate-900 overflow-hidden">
                      <img
                        src={p.photo}
                        alt=""
                        className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-110 ${p.blurred ? 'blur-2xl opacity-40' : 'blur-[4px] opacity-60'}`}
                      />
                      {p.blurred && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-2 z-10">
                          <Lock className="h-6 w-6 text-brand-pink" />
                        </div>
                      )}
                      {p.verified && (
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-wider bg-brand-pink text-white shadow-lg">
                          Verified
                        </div>
                      )}
                    </div>
                    <div className="p-3 text-center bg-slate-800/60 backdrop-blur">
                      <p className="text-[10px] font-black text-white truncate">{p.name}</p>
                      <p className="text-[8px] text-slate-500 truncate mt-0.5 font-bold uppercase tracking-tighter">{p.tags}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center justify-center gap-2 text-slate-500 text-xs font-medium">
                <div className="flex -space-x-2">
                   {[1,2,3].map(i => <div key={i} className="w-5 h-5 rounded-full border border-slate-900 bg-slate-800" />)}
                </div>
                Only premium users can view full profiles & chat
              </div>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="border-y border-white/5 bg-slate-900/50 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-16">
              <div className="flex items-center gap-4 group">
                <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-500 shadow-lg shadow-amber-500/5 group-hover:scale-110 transition-transform">
                  <Crown className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-xl font-black text-white leading-none">
                    {countsLoading ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" /> : counts.verified.toLocaleString()}+
                  </p>
                  <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest mt-1">Verified Friends</p>
                </div>
              </div>
              <div className="flex items-center gap-4 group">
                <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-brand-pink/10 border border-brand-pink/20 text-brand-pink shadow-lg shadow-brand-pink/5 group-hover:scale-110 transition-transform">
                  <Crown className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-xl font-black text-white leading-none">
                    {countsLoading ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" /> : counts.paid.toLocaleString()}+
                  </p>
                  <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest mt-1">Paid Members</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <HowItWorks />
      <SampleProfiles />
      <WhyPremium />
      <PricingSection />
      <FAQSection />
    </div>
  );
};

export default HomePage;
